import { getObjectUtil } from 'packages/src'
import REDUX_ASSOCIATION_NON_DELETABLE from '../../constants/association-non-deletable'
import { MODEL_KEY } from '../../../model/constants'

import REDUX_NON_DELETABLE from '../../constants/non-deletable'
import { shouldKeepReduxHook } from '../../hooks/should-keep'
import getAssociationKeysReduxUtil from '../get-associations-keys'
import getDefaultDomainListPathReduxUtil from '../get-default-domain-list-path'
import getDomainListPathsReduxUtil from '../get-domain-list-path'
import getDomainPathFromPathReduxUtil from '../get-domain-path-from-path'
import { getIdReduxUtil } from '../get-id'
import getInstanceReduxUtil from '../get-instance'
import { getPathReduxUtil } from '../get-path'
import getPathFromDomainPathReduxUtil from '../get-path-from-domain-path'
import { getValueReduxUtil } from '../get-value'
import isNewReduxUtil from '../is-new'
import deletePathReferenceUtil from '../../../references/utils/delete-path'
import setUtil from '../../../../utils/set'

/**
 * Deletes the instance by:
 * - Removing it from the domain lists
 * - Removing it from the domain map if it's a local instance
 * A instance that has been saved on the instance needs to be first removed
 * from the domain list. Once it's remotely deleted `afterDeleteReduxAction`
 * will remove from the domain map.
 * @param  {Object} params
 *         {Object} params.state
 *         {Object} params.instance
 *         {String} [params.domainListPath]
 *         {Boolean} [params.shouldDeleteFromReferences]
 * @return {Object}
 */
function deleteInstanceFromDomainReduxUtil (params) {
  let {
    state,
    instance,
    domainListPath,
    shouldDeleteFromReferences
  } = params

  if (!instance ||
    REDUX_NON_DELETABLE.includes(instance[MODEL_KEY.CLASS_NAME]) ||
    // e.g. exercises
    shouldKeepReduxHook(instance, state)) {
    return state
  }

  const path = getPathReduxUtil(instance)
  const associationKeys = getAssociationKeysReduxUtil(instance)
  if (associationKeys && associationKeys.length) {
    state = associationKeys.reduce((updatedState, associationKey) => {
      const associationNonDeletable = (
        REDUX_ASSOCIATION_NON_DELETABLE[instance[MODEL_KEY.CLASS_NAME]]
      )
      if (Array.isArray(associationNonDeletable) &&
        associationNonDeletable.includes(associationKey)) {
        return updatedState
      }
      const associationPointer = getValueReduxUtil(instance, associationKey)
      if (!associationPointer) {
        return updatedState
      }
      if (Array.isArray(associationPointer)) {
        return associationPointer.reduce((newUpdatedState, asso) => {
          const association = getInstanceReduxUtil(state, asso)
          if (!association ||
            REDUX_NON_DELETABLE.includes(association[MODEL_KEY.CLASS_NAME])) {
            return newUpdatedState
          }
          return deleteInstanceFromDomainReduxUtil({
            state: newUpdatedState,
            instance: association,
            shouldDeleteFromReferences
          })
        }, updatedState)
      }
      const association = getInstanceReduxUtil(state, associationPointer)
      if (!association ||
        REDUX_NON_DELETABLE.includes(association[MODEL_KEY.CLASS_NAME])) {
        return updatedState
      }
      return deleteInstanceFromDomainReduxUtil({
        state: updatedState,
        instance: association,
        shouldDeleteFromReferences
      })
    }, state)
  }

  if (!instance) {
    return state
  }

  let domainListPaths = [getDefaultDomainListPathReduxUtil(instance)]
  domainListPaths = [
    ...domainListPaths,
    ...(
      domainListPath
        ? [domainListPath]
        : getDomainListPathsReduxUtil(instance, state)
    )
  ]

  let newState = domainListPaths.reduce((updatedState, domainListPath) => {
    const domainListDataPath = domainListPath + '.data'
    const domainListData = getObjectUtil(updatedState, domainListDataPath)
    if (!domainListData || !Array.isArray(domainListData)) {
      return updatedState
    }
    const id = getIdReduxUtil(instance)
    const domainPath = getDomainPathFromPathReduxUtil(domainListPath)
    if (!domainPath) {
      return updatedState
    }

    const newDomainListData = domainListData.reduce((itemIds, itemId) => {
      const itemInstancePath = getPathFromDomainPathReduxUtil(domainPath,
        itemId
      )
      if (itemId !== id && getObjectUtil(updatedState, itemInstancePath)) {
        itemIds.push(itemId)
      }
      return itemIds
    }, [])

    return setUtil(updatedState, domainListDataPath, newDomainListData)
  }, state)

  // If the instance is not saved, delete it locally
  if (isNewReduxUtil(instance) || shouldDeleteFromReferences) {
    // Delete the path from the references
    newState = deletePathReferenceUtil({
      state: newState,
      instance
    })

    newState = setUtil(newState, path)
  }

  // If the instance saved, do not delete it locally,
  // an action will do it once the request is successful
  // @see afterDeleteReduxAction
  return newState
}

export default deleteInstanceFromDomainReduxUtil
